import { LinkProps } from "@tanstack/react-router"
import { QrCode, SquareDashedBottomCode } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useIntegrationsStatusQuery } from "modules/settings/hooks/useIntegrationsStatusQuery"
import { useWhatsappNumbersQuery } from "modules/settings/hooks/whatsapp/useWhatsappNumbersQuery"
import { GoogleIcon } from "modules/shared/icons/google"
import { InstagramIcon } from "modules/shared/icons/instagram"
import { OutlookIcon } from "modules/shared/icons/outlook"
import { WhatsappIcon } from "modules/shared/icons/whatsapp"
import { useTranslation } from "react-i18next"
import { SettingsCard, SettingsSection } from "../SettingsCard"

type ChannelOption = {
  id: string
  title: string
  description: string
  icon?: React.ElementType
  isActive?: boolean
} & ({ navigate: LinkProps } | { displayOnly: true })

export const CompanyChannels = () => {
  const { t } = useTranslation()

  const { data, isPending } = useIntegrationsStatusQuery()
  const { data: whatsappNumbers } = useWhatsappNumbersQuery()
  const { data: currentEmployee } = useAuthEmployeeQuery()

  const firstWhatsappNumber = whatsappNumbers?.[0]?.number
  const canManageWhatsapp = currentEmployee?.isOwner || currentEmployee?.isManager

  const channels: ChannelOption[] = [
    {
      id: "widget",
      title: t("settings.channels.widget.title"),
      description: t("settings.channels.widget.description"),
      icon: SquareDashedBottomCode,
      navigate: { to: "/settings/channels/widget" },
    },
    {
      id: "whatsapp",
      title: t("settings.integrations.whatsapp.title"),
      description: t("settings.integrations.whatsapp.connectedNumber", {
        number: firstWhatsappNumber,
      }),
      icon: WhatsappIcon,
      ...(canManageWhatsapp
        ? { navigate: { to: "/settings/channels/whatsapp" } }
        : { displayOnly: true as const }),
      isActive: data?.whatsapp,
    },
    {
      id: "instagram",
      title: t("settings.integrations.instagram.title"),
      description: t("settings.integrations.instagram.description"),
      icon: ({ className }: { className?: string }) => (
        <InstagramIcon width={24} className={className} />
      ),
      navigate: { to: "/settings/channels/instagram" },
      isActive: data?.instagram,
    },
    {
      id: "gmail",
      title: t("settings.integrations.gmail.title"),
      description: t("settings.integrations.gmail.description"),
      icon: ({ className }: { className?: string }) => <GoogleIcon className={className} />,
      navigate: { to: "/settings/channels/gmail" },
      isActive: data?.gmail,
    },
    {
      id: "outlook",
      title: t("settings.integrations.outlook.title"),
      description: t("settings.integrations.outlook.description"),
      icon: ({ className }: { className?: string }) => <OutlookIcon className={className} />,
      navigate: { to: "/settings/channels/outlook" },
      isActive: data?.outlook,
    },
    {
      id: "qr",
      title: t("settings.channels.qr.title"),
      description: t("settings.channels.qr.description"),
      icon: QrCode,
      navigate: { to: "/settings/channels/qr" },
    },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {channels.map((option) => {
        const hasNavigate = "navigate" in option

        let sectionExtra: { navigate: LinkProps } | { displayOnly: true }

        if (hasNavigate) {
          sectionExtra = { navigate: option.navigate }
        } else {
          sectionExtra = { displayOnly: true }
        }

        const section: SettingsSection = {
          id: option.id,
          title: option.title,
          description: option.description,
          icon: option.icon,
          isActive: !isPending && option.isActive,
          ...sectionExtra,
        }

        return <SettingsCard key={option.id} section={section} />
      })}
    </div>
  )
}
